import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { object, string, boolean, number } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  useCreateDynamicField,
  useUpdateDynamicField,
  useGetFieldTypeOptions,
  type DynamicFieldParamsType,
} from 'src/pages/DynamicFieldsPage/api';
import Dropdown from 'src/components/Dropdown';
import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import { PencilAdornment } from 'src/utils/components';
import { useGetFieldCategories } from 'src/pages/SetupPage/api';

import messages from './messages';
import DynamicFieldOptionForm from '../DynamicFieldOptionForm';

interface DynamicFieldFormProps {
  create: boolean;
  dynamicField?: UCM.ExtendedDynamicFieldType;
}

export default function DynamicFieldForm({
  create,
  dynamicField,
}: DynamicFieldFormProps) {
  const { formatMessage } = useIntl();
  const { uuid: fieldCategoryUuid } = useParams<{ uuid: string }>();

  const [open, setOpen] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const booleanOptions = useMemo(
    () => [
      { value: true, label: formatMessage(messages.yesLabel) },
      { value: false, label: formatMessage(messages.noLabel) },
    ],
    [],
  );

  const { data: fieldCategories } = useGetFieldCategories();
  const { data: fieldTypeOptions } = useGetFieldTypeOptions();

  const { mutate: createDynamicField, isPending: isCreating } =
    useCreateDynamicField();
  const { mutate: updateDynamicField, isPending: isUpdating } =
    useUpdateDynamicField();

  const formatFieldCategories = useMemo(() => {
    return fieldCategories?.map((category) => ({
      value: category.uuid,
      label: category.name.en,
    }));
  }, [fieldCategories]);

  const isSelectable = useCallback((code: string) => {
    return code === 'single-selectable' || code === 'multi-selectable';
  }, []);

  const initialValues = useMemo(
    () => ({
      nameEn: '',
      nameDe: '',
      position: 0,
      typeCode: '',
      description: '',
      questionEn: '',
      questionDe: '',
      helpTextEn: '',
      helpTextDe: '',
      isVisibleByAdmin: true,
      isVisibleByUser: false,
      categoryUuid: fieldCategoryUuid ?? '',
    }),
    [fieldCategoryUuid],
  );

  const formik = useFormik<DynamicFieldParamsType>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      nameEn: string().required(),
      nameDe: string().required(),
      questionEn: string().required(),
      questionDe: string().required(),
      description: string().required(),
      typeCode: string().required(),
      isVisibleByUser: boolean().required(),
      categoryUuid: string().required(),
      isVisibleByAdmin: boolean().required(),
      position: number().integer().min(0).required(),
      helpTextEn: string(),
      helpTextDe: string(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  // Set the values of the form when the tutorial is opened in edit mode
  useEffect(() => {
    if (dynamicField && !create && open) {
      formik.setValues({
        nameEn: dynamicField.nameEn,
        nameDe: dynamicField.nameDe,
        position: dynamicField.position,
        typeCode: dynamicField.type.code,
        questionEn: dynamicField.questionEn,
        questionDe: dynamicField.questionDe,
        helpTextEn: dynamicField.helpTextEn,
        helpTextDe: dynamicField.helpTextDe,
        description: dynamicField.description,
        categoryUuid: dynamicField.category.uuid,
        isVisibleByUser: dynamicField.isVisibleByUser,
        isVisibleByAdmin: dynamicField.isVisibleByAdmin,
      });
    }
  }, [open, dynamicField, create]);
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    formik.resetForm();
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      const mutationOptions = {
        onSuccess: () => {
          resolve(true);
        },
        onError: () => {
          resolve(false);
        },
      };

      if (create) {
        createDynamicField(values, mutationOptions);
      }

      if (!create && dynamicField) {
        updateDynamicField(
          { ...values, dynamicFieldUuid: dynamicField.uuid },
          mutationOptions,
        );
      }
    });
  }, [create, values, dynamicField]);

  return (
    <ConfirmModal
      width="70%"
      onOpen={handleOpen}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title={formatMessage(messages.title)}
      loading={isCreating || isUpdating}
      triggerButton={(handleOpen) =>
        create ? (
          <Button
            variant="outlined"
            onClick={handleOpen}
            startIcon={<AddOutlinedIcon />}
          >
            {formatMessage(messages.createDynamicFieldButtonLabel)}
          </Button>
        ) : (
          <Tooltip arrow title={formatMessage(messages.editTooltip)}>
            <IconButton onClick={handleOpen}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="nameEn"
            value={values.nameEn}
            error={!!errors.nameEn}
            helperText={errors.nameEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.nameEnLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="nameDe"
            value={values.nameDe}
            error={!!errors.nameDe}
            helperText={errors.nameDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.nameDeLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="position"
            value={values.position}
            error={!!errors.position}
            helperText={errors.position}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.positionLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            fullWidth
            name="isVisibleByAdmin"
            value={values.isVisibleByAdmin}
            options={booleanOptions}
            onChange={formik.handleChange}
            helperText={errors.isVisibleByAdmin}
            error={!!errors.isVisibleByAdmin}
            label={formatMessage(messages.visibleByAdminLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            fullWidth
            name="isVisibleByUser"
            value={values.isVisibleByUser}
            options={booleanOptions}
            onChange={formik.handleChange}
            helperText={errors.isVisibleByUser}
            error={!!errors.isVisibleByUser}
            label={formatMessage(messages.visibleByUserLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            fullWidth
            name="categoryUuid"
            value={values.categoryUuid}
            options={formatFieldCategories}
            onChange={formik.handleChange}
            helperText={errors.categoryUuid}
            error={!!errors.categoryUuid}
            label={formatMessage(messages.categoryLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Dropdown
            fullWidth
            name="typeCode"
            value={values.typeCode}
            options={fieldTypeOptions}
            onChange={formik.handleChange}
            helperText={errors.typeCode}
            error={!!errors.typeCode}
            label={formatMessage(messages.typeLabel)}
          />
        </Grid>

        <Grid size={12}>
          <TextInput
            fullWidth
            name="description"
            value={values.description}
            error={!!errors.description}
            helperText={errors.description}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.descriptionLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="questionEn"
            value={values.questionEn}
            error={!!errors.questionEn}
            helperText={errors.questionEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.questionEnLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="questionDe"
            value={values.questionDe}
            error={!!errors.questionDe}
            helperText={errors.questionDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.questionDeLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="helpTextEn"
            value={values.helpTextEn}
            error={!!errors.helpTextEn}
            helperText={errors.helpTextEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.helpTextEnLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="helpTextDe"
            value={values.helpTextDe}
            error={!!errors.helpTextDe}
            helperText={errors.helpTextDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.helpTextDeLabel)}
          />
        </Grid>

        {!create && isSelectable(values.typeCode) && dynamicField && (
          <DynamicFieldOptionForm dynamicField={dynamicField} />
        )}
      </Grid>
    </ConfirmModal>
  );
}
